import type { SearchFilters } from '@content-search/domain';
import { shouldRefreshDateRange, getDefaultDateRange } from '@content-search/utils';
import { useCallback, useState } from 'react';

import { FilterPanel } from './FilterPanel/FilterPanel';
import { SearchInput } from './SearchInput';
import * as styles from './SearchPanel.css';

export interface SearchPanelProps {
  searchFilters: SearchFilters;
  setSearchFilters: (filters: SearchFilters) => void;
  isLoading?: boolean;
  onSearch: (filters: SearchFilters) => void;
}

export function SearchPanel({ searchFilters, setSearchFilters, isLoading = false, onSearch }: SearchPanelProps) {
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

  const handleToggleFiltersPanel = useCallback(() => {
    setIsFiltersPanelOpen(!isFiltersPanelOpen);
  }, [isFiltersPanelOpen]);

  // Handle apply or clear filters from FilterPanel
  const handleApplyOrClearFilters = useCallback(
    (newSearchFilters: SearchFilters) => {
      // Check if we should refresh the date range to "now"
      let finalFilters = { ...newSearchFilters };

      if (newSearchFilters.panelFilters.startDate && newSearchFilters.panelFilters.endDate) {
        // If the current date range appears to be a default range, refresh it to "now"
        if (shouldRefreshDateRange(newSearchFilters.panelFilters.startDate, newSearchFilters.panelFilters.endDate)) {
          const freshDateRange = getDefaultDateRange();

          finalFilters = {
            ...finalFilters,
            panelFilters: {
              ...finalFilters.panelFilters,
              startDate: freshDateRange.startDate,
              endDate: freshDateRange.endDate,
            },
          };
        }
      }

      // Update search filters and trigger search
      setSearchFilters(finalFilters);
      onSearch(finalFilters);

      // Close the filter panel
      setIsFiltersPanelOpen(false);
    },
    [setSearchFilters, onSearch]
  );

  const updateSearchFilters = useCallback(
    (updates: Partial<SearchFilters>) => {
      const newFilters = {
        ...searchFilters,
        ...updates,
        // Handle nested panelFilters updates properly
        panelFilters: updates.panelFilters ? { ...searchFilters.panelFilters, ...updates.panelFilters } : searchFilters.panelFilters,
      };

      setSearchFilters(newFilters);
    },
    [searchFilters, setSearchFilters]
  );

  // Render the component
  return (
    <div className={styles.contentFiltersContainer}>
      {/* Search Options Card */}
      <SearchInput
        isFiltersPanelOpen={isFiltersPanelOpen}
        isLoading={isLoading}
        searchFilters={searchFilters}
        toggleFiltersPanel={handleToggleFiltersPanel}
        updateSearchFilters={updateSearchFilters}
        onSearch={onSearch}
      />

      {/* Filter Panel */}
      <FilterPanel isLoading={isLoading} isOpen={isFiltersPanelOpen} searchFilters={searchFilters} onApplyOrClearFilters={handleApplyOrClearFilters} />
    </div>
  );
}
